import { useState, useMemo } from 'react';
import dayjs, { Dayjs } from 'dayjs';
import clsx from 'clsx';
import { DateRangeTypes, PaletteUtils } from '@shared/utils';
import { RadioInputOrientationTypeEnum } from '@shared/widgets/inputs/radio/models';
import { DateRangePickerInput, Icon, Modal, RadioInput, Text } from '@shared/widgets';
import ChevronIcon from '@assets/icons/chevron.svg';
import CloseIcon from '@assets/icons/close.svg';

interface IProps {
  className?: string;
  nullValue?: { isEnable: boolean; text?: string };
  value?: { dateFrom?: Dayjs; dateTo?: Dayjs };
  onChange: (value?: { dateFrom?: Dayjs; dateTo?: Dayjs }) => void;
}

const periodRadioOptions = [
  {
    text: 'Last day',
    value: DateRangeTypes.LAST_DAY,
  },
  {
    text: 'Last 15 days',
    value: DateRangeTypes.LAST_15_DAYS,
  },
  {
    text: 'Last 30 days',
    value: DateRangeTypes.LAST_30_DAYS,
  },
  {
    text: 'Last quarter',
    value: DateRangeTypes.LAST_QUARTER,
  },
  {
    text: 'Last year',
    value: DateRangeTypes.LAST_YEAR,
  },
  {
    text: 'Manual',
    value: DateRangeTypes.MANUAL,
  },
];

const defaultNullValueText = 'Period';
export const SelectDateRange = (props: IProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedPeriod, setSelectedPeriod] = useState<string>();
  const [isManual, setIsManual] = useState(false);

  const currentLabel = useMemo(() => {
    if (!props.value || (!props.value.dateFrom && !props.value.dateTo)) {
      return props.nullValue?.text ?? defaultNullValueText;
    }
    return `${props.value.dateFrom?.toDate()?.toLocaleDateString('en-GB') ?? ''} - ${props.value.dateTo?.toDate()?.toLocaleDateString('en-GB') ?? ''}`;
  }, [props.value]);

  const onChangeRadio = (value: DateRangeTypes) => {
    const today = dayjs();
    switch (value) {
      case DateRangeTypes.LAST_DAY:
        props.onChange({ dateFrom: today.add(-1, 'day'), dateTo: today });
        break;
      case DateRangeTypes.LAST_15_DAYS:
        props.onChange({ dateFrom: today.add(-15, 'day'), dateTo: today });
        break;
      case DateRangeTypes.LAST_30_DAYS:
        props.onChange({ dateFrom: today.add(-30, 'day'), dateTo: today });
        break;
      case DateRangeTypes.LAST_QUARTER: {
        const quarter = Math.floor(today.month() / 3);
        const startFullQuarter = new Date(today.year(), quarter * 3 - 3, 1);
        const endFullQuarter = new Date(startFullQuarter.getFullYear(), startFullQuarter.getMonth() + 3, 0);
        props.onChange({
          dateFrom: dayjs(startFullQuarter),
          dateTo: dayjs(endFullQuarter),
        });
        break;
      }
      case DateRangeTypes.LAST_YEAR:
        props.onChange({ dateFrom: today.add(-1, 'year'), dateTo: today });
        break;
      case DateRangeTypes.MANUAL:
        break;
      default:
        break;
    }

    const isManual = value == DateRangeTypes.MANUAL;
    setIsManual(isManual);
  };

  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation();
    props.onChange(undefined);
    setSelectedPeriod(undefined);
    setIsManual(false);
  };

  return (
    <div className={clsx('flex relative', props.className)}>
      <div
        className='flex w-full h-10 border border-transparent border-b-light hover:border hover:border-primary bg-ice hover:bg-white dark:bg-soft-black cursor-pointer'
        onClick={() => setIsOpen(!isOpen)}
      >
        <Text weight='light' className='h-6 my-2 mx-3 text-center text-nowrap '>
          {currentLabel ?? props.nullValue?.text ?? defaultNullValueText}
        </Text>
        <div className='flex items-center'>
          <div className={clsx('flex items-center justify-center w-4 h-10 relative -top-[1px] right-[3px]')}>
            {props.value && (props.value.dateFrom || props.value.dateTo) && <Icon icon={<CloseIcon className='w-2 h-2' />} onClick={handleClear} />}
          </div>
          <div
            className={clsx(
              'flex items-center justify-center w-12 h-10 relative -top-[1px] -right-[1px]',
              PaletteUtils.isInsigniaPalette && 'bg-soft-black dark:bg-slate',
              PaletteUtils.isVcfcPalette && 'bg-primary',
            )}
          >
            <Icon icon={<ChevronIcon />} className='-rotate-90' color={PaletteUtils.getColor('primary', 'white')} />
          </div>
        </div>
      </div>
      <Modal isOpen={isOpen} title='Choose period' onClose={() => setIsOpen(false)}>
        <RadioInput
          options={periodRadioOptions}
          orientation={RadioInputOrientationTypeEnum.Vertical}
          selected={selectedPeriod}
          onChange={(val) => {
            setSelectedPeriod(val);
            onChangeRadio(val as DateRangeTypes);
          }}
        />
        <div className='m-5'>
          <DateRangePickerInput
            dateFrom={props.value?.dateFrom}
            dateTo={props.value?.dateTo}
            isReadOnly={!isManual}
            onChange={(from, to) => {
              if (from === null || to === null) return;
              props.onChange({ dateFrom: from, dateTo: to });
            }}
          />
        </div>
      </Modal>
    </div>
  );
};
